/**
 * LT-3320 — how a cell alert names the sensor it is about.
 *
 * A tag-scoped alert carries `tag_id` (an equipment_data_tag id, LT-3319). Its message
 * leads with the raw dotted tag path — process_monitor writes `"<tag>: <reason>"`, e.g.
 * `ExtruderTP1.BarrelZone2.C.Actual: excess variation` — which is the PLC's name for the
 * signal, not the plant's. The plant's name lives in the cell's equipment tree:
 *
 *     <equipment alias> [component alias] <sensor alias>
 *
 * The component alias is only included when the equipment has more than one component,
 * because only then is it what tells two sensors apart: an oven's ten zones are ten
 * components that each hold a `BurnerGasFlow`, so `Cure oven 1. BurnerGasFlow` names ten
 * sensors while `Cure oven 1. Zone10 BurnerGasFlow` names one. On single-component
 * equipment the component alias is a re-spelling of the equipment alias
 * (`Extruder 4  - 1" Microtruder` vs `Extruder 4 Microtruder 1"`) and only adds noise.
 * Measured on dev 2026-10-02: unique across every controlled sensor of Spartanburg (395),
 * Aguascalientes (1,736) and New Philadelphia 2 (25 — one pair of equipment shares the
 * alias `Puller 1`, a data fix).
 *
 * This deliberately does NOT reuse the envelope's `tag_alias` as the primary name: that is
 * componentAlias + dataTag, which drops the equipment on multi-component equipment. It is
 * the fallback when the tree has not loaded or does not hold the tag.
 */
import type { EquipmentTreeEntity } from '@liveline/ll_node_common/lib/models/api/equipment';
import type { CellAlert } from '@liveline/ll_node_common/lib/models/websocket';

/** One sensor of a cell, as the alert feed needs it. */
export interface CatalogueTag {
    tagId: number;
    /** `<equipment alias> [component alias] <sensor alias>`, or null when every part is blank. */
    label: string | null;
    /** The raw dotted tag path, e.g. `ExtruderTP1.BarrelZone2.C.Actual`. */
    tag: string | null;
}

/** A cell's sensors keyed by equipment_data_tag id, or where its fetch has got to. */
export type CellTagCatalogue =
    | { status: 'loading' }
    | { status: 'ready'; tags: Map<number, CatalogueTag> }
    | { status: 'failed' };

/** The sensor an alert is about, resolved as far as the catalogue allows. */
export interface AlertSensor {
    tagId: number;
    /** Display name; null leaves the caller's "Tag {id}" fallback in charge. */
    label: string | null;
    tag: string | null;
    /** The cell's catalogue has not answered yet — the row keeps its plain look until it does. */
    pending: boolean;
}

/**
 * Trimmed, whitespace-collapsed text, or null for blank. The catalogue sends the literal
 * string "null" for an unset alias (same hazard mobile's cleanText guards), and some
 * aliases carry doubled spaces (`Extruder 4  - 1" Microtruder`).
 */
export const cleanAlias = (value: unknown): string | null => {
    if (typeof value !== 'string') return null;
    const text = value.replace(/\s+/g, ' ').trim();
    return text && text !== 'null' && text !== 'undefined' ? text : null;
};

/** Spelling-insensitive identity: `Barrel Zone 3` and `BarrelZone3` are the same words. */
const sameWords = (text: string): string => text.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Join the three levels, keeping the component only on multi-component equipment and
 * dropping any part that repeats an earlier one, so no `6th Puller Puller Speed`.
 */
export const sensorLabel = (parts: {
    equipment: unknown;
    component: unknown;
    sensor: unknown;
    componentCount: number;
}): string | null => {
    const candidates = [
        cleanAlias(parts.equipment),
        parts.componentCount > 1 ? cleanAlias(parts.component) : null,
        cleanAlias(parts.sensor),
    ];
    const kept: string[] = [];
    candidates.forEach((part) => {
        if (!part) return;
        const words = sameWords(part);
        if (!words || kept.some((k) => sameWords(k) === words)) return;
        kept.push(part);
    });
    return kept.length ? kept.join(' ') : null;
};

const rawTag = (value: unknown): string | null =>
    typeof value === 'string' && value.trim() ? value.trim() : null;

/** Flatten a cell's equipment tree into its sensors, each named by the rule above. */
export const buildTagCatalogue = (equipment: EquipmentTreeEntity[] | undefined | null): Map<number, CatalogueTag> => {
    const tags = new Map<number, CatalogueTag>();
    (equipment ?? []).forEach((eq) => {
        const components = eq?.components ?? [];
        components.forEach((component) => {
            (component?.elements ?? []).forEach((element) => {
                // Older backends spelled the field `equipmentCompnentElementAlias` (see useTagIngestion).
                const elementAlias =
                    cleanAlias(element?.equipmentComponentElementAlias) ??
                    cleanAlias((element as any)?.equipmentCompnentElementAlias);
                (element?.dataTags ?? []).forEach((dataTag) => {
                    const tagId = dataTag?.equipmentDataTagId;
                    if (typeof tagId !== 'number') return;
                    tags.set(tagId, {
                        tagId,
                        label: sensorLabel({
                            equipment: cleanAlias(eq?.equipmentAlias) ?? dataTag.equipmentAlias,
                            component: cleanAlias(component?.componentAlias) ?? dataTag.componentAlias,
                            // An element alias is set on every dev element measured, but a blank one
                            // must still name the sensor rather than leave just the equipment.
                            sensor: elementAlias ?? cleanAlias(dataTag.dataTag) ?? cleanAlias(dataTag.shortTagName),
                            componentCount: components.length,
                        }),
                        tag: rawTag(dataTag.tag),
                    });
                });
            });
        });
    });
    return tags;
};

/**
 * The sensor an alert names, or null when it names none (RUN_STARTED, KAFKA_DATA_STALE…).
 * `catalogue` is that alert's cell, undefined until it has been requested.
 */
export const resolveAlertSensor = (alert: CellAlert, catalogue: CellTagCatalogue | undefined): AlertSensor | null => {
    const tagId = alert?.tag_id;
    if (typeof tagId !== 'number' || !Number.isFinite(tagId)) return null;
    const known = catalogue?.status === 'ready' ? catalogue.tags.get(tagId) : undefined;
    return {
        tagId,
        label: known?.label ?? cleanAlias(alert.tag_alias),
        tag: known?.tag ?? null,
        pending: !catalogue || catalogue.status === 'loading',
    };
};

/**
 * What follows `"<tag>: "` when the message leads with EXACTLY this sensor's raw tag, else
 * null. Display only: the sensor itself always comes from `tag_id`, never from the text, so
 * a message that does not lead with its own tag is shown as written.
 */
export const messageAfterTag = (message: string, tag: string | null): string | null => {
    if (!tag || typeof message !== 'string') return null;
    const prefix = `${tag}: `;
    return message.startsWith(prefix) ? message.slice(prefix.length) : null;
};

/**
 * Plain-text form for places with no room for a hover (ticker, toasts): the raw tag swapped
 * for the sensor's name, or the name put in front of a message that does not lead with it.
 * Unchanged while the catalogue is pending, or when there is no sensor or no name.
 */
export const formatAlertMessage = (alert: CellAlert, sensor: AlertSensor | null): string => {
    const message = alert?.message ?? '';
    if (!sensor || sensor.pending || !sensor.label) return message;
    const rest = messageAfterTag(message, sensor.tag);
    return rest !== null ? `${sensor.label}: ${rest}` : `${sensor.label} · ${message}`;
};
